import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Phone, CalendarBlank, Plus } from "@phosphor-icons/react";
import { getMeetingStatusClass } from "../config/uiHelpers";

const Meetings = ({ user, logout }) => {
  const [meetings, setMeetings] = useState([]);
  const [calls, setCalls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("meetings");
  const [showForm, setShowForm] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ title: "", contact_id: "", contact_name: "", scheduled_at: "", duration_mins: 60, notes: "" });
  const [callForm, setCallForm] = useState({ contact_id: "", contact_name: "", duration_mins: 15, notes: "", outcome: "connected" });

  const fetchAll = useCallback(async () => {
    try {
      const [mRes, cRes, ctRes] = await Promise.all([
        axios.get(`${API}/crm/meetings`),
        axios.get(`${API}/crm/calls`),
        api.get(`${API}/crm/contacts`)
      ]);
      setMeetings(mRes.data);
      setCalls(cRes.data);
      setContacts(ctRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const scheduleMeeting = async (e) => {
    e.preventDefault();
    const contact = contacts.find(c => c.contact_id === meetingForm.contact_id);
    try {
      await axios.post(`${API}/crm/meetings`, {
        title: meetingForm.title,
        contact_id: meetingForm.contact_id,
        contact_name: contact?.name || '',
        scheduled_at: meetingForm.scheduled_at,
        duration_mins: meetingForm.duration_mins,
        notes: meetingForm.notes,
        created_by: user.user_id,
        created_by_name: user.name
      });
      toast.success("Meeting scheduled!");
      setShowForm(false);
      setMeetingForm({ title: "", contact_id: "", contact_name: "", scheduled_at: "", duration_mins: 60, notes: "" });
      fetchAll();
    } catch (err) { toast.error("Failed to schedule meeting"); }
  };

  const logCall = async (e) => {
    e.preventDefault();
    const contact = contacts.find(c => c.contact_id === callForm.contact_id);
    try {
      await axios.post(`${API}/crm/calls?contact_id=${callForm.contact_id}&contact_name=${encodeURIComponent(contact?.name || '')}&duration_mins=${callForm.duration_mins}&notes=${encodeURIComponent(callForm.notes)}&outcome=${callForm.outcome}&logged_by=${user.user_id}&logged_by_name=${encodeURIComponent(user.name)}`);
      toast.success("Call logged!");
      setShowForm(false);
      setCallForm({ contact_id: "", contact_name: "", duration_mins: 15, notes: "", outcome: "connected" });
      fetchAll();
    } catch (err) { toast.error("Failed to log call"); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Communication Hub</h1>
            <p className="text-base text-slate-700">Meetings, calls, and scheduling</p>
          </div>
          <button data-testid="new-entry-btn" onClick={() => setShowForm(!showForm)}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300 flex items-center gap-2">
            <Plus size={18} /> {showForm ? 'Cancel' : (tab === 'meetings' ? 'Schedule Meeting' : 'Log Call')}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button data-testid="tab-meetings" onClick={() => { setTab("meetings"); setShowForm(false); }}
            className={`rounded-none px-6 py-2 font-medium transition-colors duration-300 ${tab === 'meetings' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-800'}`}>
            Meetings ({meetings.length})
          </button>
          <button data-testid="tab-calls" onClick={() => { setTab("calls"); setShowForm(false); }}
            className={`rounded-none px-6 py-2 font-medium transition-colors duration-300 ${tab === 'calls' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-800'}`}>
            Call Log ({calls.length})
          </button>
        </div>

        {/* Forms */}
        {showForm && tab === "meetings" && (
          <div data-testid="meeting-form" className="bg-white border border-slate-200 rounded-none p-8 mb-6 shadow-sm">
            <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Schedule Meeting</h3>
            <form onSubmit={scheduleMeeting} className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Title</label>
                <input data-testid="meeting-title" type="text" value={meetingForm.title} onChange={e => setMeetingForm({...meetingForm, title: e.target.value})} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Contact</label>
                <select data-testid="meeting-contact" value={meetingForm.contact_id} onChange={e => setMeetingForm({...meetingForm, contact_id: e.target.value})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                  <option value="">Select Contact</option>
                  {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name} - {c.company}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Date & Time</label>
                <input data-testid="meeting-datetime" type="datetime-local" value={meetingForm.scheduled_at} onChange={e => setMeetingForm({...meetingForm, scheduled_at: e.target.value})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Duration (mins)</label>
                <input type="number" value={meetingForm.duration_mins} onChange={e => setMeetingForm({...meetingForm, duration_mins: parseInt(e.target.value)})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" />
              </div>
              <div className="col-span-2">
                <button type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium">Schedule</button>
              </div>
            </form>
          </div>
        )}

        {showForm && tab === "calls" && (
          <div data-testid="call-form" className="bg-white border border-slate-200 rounded-none p-8 mb-6 shadow-sm">
            <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Log Call</h3>
            <form onSubmit={logCall} className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Contact</label>
                <select value={callForm.contact_id} onChange={e => setCallForm({...callForm, contact_id: e.target.value})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" required>
                  <option value="">Select Contact</option>
                  {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Outcome</label>
                <select value={callForm.outcome} onChange={e => setCallForm({...callForm, outcome: e.target.value})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                  <option value="connected">Connected</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="no_answer">No Answer</option>
                  <option value="follow_up">Follow Up Needed</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Duration (mins)</label>
                <input type="number" value={callForm.duration_mins} onChange={e => setCallForm({...callForm, duration_mins: parseInt(e.target.value)})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Notes</label>
                <input type="text" value={callForm.notes} onChange={e => setCallForm({...callForm, notes: e.target.value})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" required />
              </div>
              <div className="col-span-2">
                <button type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium">Log Call</button>
              </div>
            </form>
          </div>
        )}

        {/* List */}
        {loading ? <div className="flex justify-center py-20"><div className="loading-spinner"></div></div> : (
          <div className="space-y-4">
            {tab === "meetings" && meetings.map(m => (
              <div key={m.meeting_id} className="bg-white border border-slate-200 rounded-none p-5 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-none"><CalendarBlank size={24} weight="fill" className="text-[#002FA7]" /></div>
                <div className="flex-1">
                  <h4 className="font-semibold">{m.title}</h4>
                  <p className="text-sm text-slate-600">{m.contact_name} | {new Date(m.scheduled_at).toLocaleString()} | {m.duration_mins} min</p>
                </div>
                <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase border ${getMeetingStatusClass(m.status)}`}>{m.status}</span>
              </div>
            ))}
            {tab === "calls" && calls.map(c => (
              <div key={c.call_id} className="bg-white border border-slate-200 rounded-none p-5 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-none"><Phone size={24} weight="fill" className="text-[#10B981]" /></div>
                <div className="flex-1">
                  <h4 className="font-semibold">{c.contact_name}</h4>
                  <p className="text-sm text-slate-600">{c.duration_mins} min | {c.notes}</p>
                </div>
                <span className="rounded-none px-2 py-1 text-xs font-bold uppercase bg-slate-100 text-slate-700 border border-slate-200">{c.outcome}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Meetings;
